import { ReactNode, useEffect, useState } from "react"

import { ArrowLeftIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import { Link, useFetcher, useLoaderData } from "react-router-dom"
import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"
import { InventoryItemDetails, InventoryItemDetailsProps } from "~/src/components/Dashboard/InventoryItemDetails"
import { MainHeading } from "~/src/components/MainHeading"
import { iname, PathBuilder } from "~/src/lib/iname"
import { randomAlphaNumeric } from "~/src/lib/string"

export type CollectionItem = InventoryItemDetailsProps & {
  collectionableType: string
  collectionableId: string
  clientName: string
  clientDescription: string
  forcePrice: number
  prepaid?: boolean
  hidden?: boolean
  maxQuantity?: number
  maxQuantityEnabled?: boolean
  inventoryNotificationThreshold?: string | null
  inventoryNotificationEnabled?: boolean
  hiddenFromInventory?: boolean
  hiddenFromStore?: boolean
  hasProductSettings?: boolean
  storeItemHref?: string
}

export type DashboardStoreCollectionItemSettingsProps = {
  title: string
  backHref: string
  name: string
  reorderHref?: string
  storeUrl: string
  item: CollectionItem
}

export type DashboardStoreCollectionItemSettingsErrors = {
  errors?: {
    collectionable?: {
      clientName?: string[]
      clientDescription?: string[]
      forcePrice?: string[]
      hidden?: string[]
      maxQuantity?: string[]
      inventoryNotificationThreshold?: string[]
      hiddenFromInventory?: string[]
    }
  }
}

const itemName = iname().collectionItem

export function DashboardStoreCollectionItemSettings() {
  const {
    title,
    backHref,
    item: { hasProductSettings, ...item },
    reorderHref,
  } = useLoaderData<DashboardStoreCollectionItemSettingsProps>()

  const fetcher = useFetcher<DashboardStoreCollectionItemSettingsErrors>()
  const { errors } = fetcher.data ?? {}

  // Whenever a form submission is completed, the following generates a random string used as a key for
  // certain fields to force invalidation of the component resulting in resetting the `defaultValue`. Currently used
  // with `clientDescription`, because the value might change significantly after normalization on the backend.
  const [reKey, setReKey] = useState(() => randomAlphaNumeric())

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data) {
      setReKey(randomAlphaNumeric())
    }
  }, [fetcher.state, fetcher.data])

  return (
    <div className="my-3 flex flex-col bg-gray-50 @container">
      <Link
        to={backHref}
        className="mx-6 mr-auto flex cursor-pointer items-center gap-2 text-lg text-gray-500 hover:underline"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Back
      </Link>

      <hgroup className="mx-6 flex flex-col items-center @6xl:mx-6 @6xl:flex-row @6xl:gap-4">
        <MainHeading title={title} className="ml-0 mr-auto" />
        <div className="mb-4 flex w-full flex-col justify-between gap-2 @2xl:flex-row @2xl:justify-end @6xl:m-0 @6xl:w-fit">
          {reorderHref ? (
            <Bee.ButtonAnchor href={reorderHref} kind="secondary" className="flex items-center gap-3">
              <ArrowPathIcon className="h-6 w-6" />
              <span>Reorder item</span>
            </Bee.ButtonAnchor>
          ) : null}

          {!item.hiddenFromStore && item.storeItemHref && (
            <Bee.ButtonAnchor href={item.storeItemHref} kind="secondary" className="flex items-center gap-2">
              <ArrowTopRightOnSquareIcon className="h-6 w-6" />
              <span>View product in store</span>
            </Bee.ButtonAnchor>
          )}
        </div>
      </hgroup>

      <fetcher.Form method="patch" className="flex flex-col gap-6 @2xl:mx-6">
        <Bee.HiddenInput name={itemName.collectionableType} value={item.collectionableType} />
        <Bee.HiddenInput name={itemName.collectionableId} value={item.collectionableId} />
        <Bee.HiddenInput name={itemName.collectionableAttributes.id} value={item.collectionableId} />

        <div className="flex flex-col divide-y border border-gray-200 bg-white @2xl:rounded-lg">
          <section className="flex flex-col gap-6 p-6">
            <InventoryItemDetails {...item} />
          </section>

          <section className="flex flex-col gap-6 p-6">
            <hgroup className="flex flex-col">
              <h1 className="text-lg font-medium text-gray-900">Display Information</h1>
              <p className="text-sm text-gray-500">
                Editing the information in this section will update how the product appears on your store site
              </p>
            </hgroup>

            <div className="flex flex-col gap-6">
              <SettingsField
                label="Display name"
                name={itemName.collectionableAttributes.clientName}
                errors={errors?.collectionable?.clientName}
              >
                <Bee.Input className="w-full @xl:w-auto @xl:grow @2xl:col-start-2" defaultValue={item.clientName} />
              </SettingsField>

              <SettingsField
                label="Description"
                name={itemName.collectionableAttributes.clientDescription}
                errors={errors?.collectionable?.clientDescription}
              >
                <Bee.TextArea
                  autoSize
                  key={"client-description-" + reKey}
                  className="w-full @xl:w-auto @xl:grow @2xl:col-start-2"
                  defaultValue={item.clientDescription}
                />
              </SettingsField>

              <SettingsField
                label="Price"
                name={itemName.collectionableAttributes.forcePrice}
                errors={errors?.collectionable?.forcePrice}
                hint={item.prepaid ? "Prepaid items cannot be priced" : undefined}
              >
                <Bee.Input
                  className="w-full disabled:cursor-not-allowed disabled:bg-gray-100 @xl:w-auto @xl:grow @2xl:col-start-2"
                  defaultValue={item.forcePrice}
                  disabled={item.prepaid}
                />
              </SettingsField>

              <SettingsToggleField
                label="Hide from store"
                name={itemName.collectionableAttributes.hidden}
                errors={errors?.collectionable?.hidden}
                hint="Hide this product from users on your storefront website, but keep it in your inventory list"
              >
                <Bee.Toggle
                  className="mt-1 shrink-0"
                  defaultChecked={item.hidden}
                  name={itemName.collectionableAttributes.hidden}
                />
              </SettingsToggleField>
            </div>
          </section>

          <section className={twMerge("flex flex-col gap-6 p-6", !hasProductSettings && "hidden")}>
            <h1 className="text-lg font-medium text-gray-900">Product Settings</h1>

            <SettingsToggleField
              label="Hide product from inventory"
              name={itemName.collectionableAttributes.hiddenFromInventory}
              errors={errors?.collectionable?.hiddenFromInventory}
              hint="Hide this product from your inventory list. You can view hidden inventory items by clicking “View hidden products” on the Inventory page."
              hidden={!hasProductSettings}
            >
              <Bee.Toggle
                className="mt-1 shrink-0"
                defaultChecked={item.hiddenFromInventory}
                name={itemName.collectionableAttributes.hiddenFromInventory}
              />
            </SettingsToggleField>

            <SettingsToggleField
              label="Enable reorder reminder"
              name={itemName.collectionableAttributes.inventoryNotificationThreshold}
              errors={errors?.collectionable?.inventoryNotificationThreshold}
              hint={
                <p className="@2xl:ml-2">
                  Send an email when the inventory drops below&nbsp;
                  <Bee.Input
                    name={itemName.collectionableAttributes.inventoryNotificationThreshold}
                    defaultValue={item.inventoryNotificationThreshold ?? 0}
                    className="w-20"
                    type="number"
                    min={0}
                  />
                  &nbsp;unit(s).
                </p>
              }
              hidden={!hasProductSettings}
            >
              <Bee.Toggle
                className="mt-1 shrink-0"
                name={itemName.collectionableAttributes.inventoryNotificationEnabled}
                defaultChecked={item.inventoryNotificationEnabled ?? false}
              />
            </SettingsToggleField>

            <SettingsToggleField
              label="Enable 'per order' limit"
              name={itemName.collectionableAttributes.maxQuantity}
              errors={errors?.collectionable?.maxQuantity}
              hint={
                <p className="@2xl:ml-2">
                  Users can only order&nbsp;
                  <Bee.Input
                    name={itemName.collectionableAttributes.maxQuantity}
                    defaultValue={item.maxQuantity ?? 0}
                    className="w-20"
                    type="number"
                    min={0}
                  />
                  &nbsp;unit(s) of this product per order.
                </p>
              }
              hidden={!hasProductSettings}
            >
              <Bee.Toggle
                className="mt-1 shrink-0"
                name={itemName.collectionableAttributes.maxQuantityEnabled}
                defaultChecked={item.maxQuantityEnabled ?? false}
              />
            </SettingsToggleField>
          </section>
        </div>

        <div className="mx-6 flex @2xl:mx-0">
          <Bee.Button className="grow" type="submit">
            Save updates
          </Bee.Button>
        </div>
      </fetcher.Form>
    </div>
  )
}

// #region Private
type SettingsFieldProps = {
  label: ReactNode
  name: string | PathBuilder
  errors?: string[]
  children?: ReactNode
  hint?: ReactNode
  className?: string
}

function SettingsField(props: SettingsFieldProps) {
  const { label, name, errors, children, hint, className } = props

  return (
    <Bee.FieldProvider label={label} name={name} errorMessages={errors}>
      <div className={twMerge("grid grid-cols-1 gap-x-4 gap-y-1 @2xl:grid-cols-[10rem,1fr]", className)}>
        <Bee.FieldLabel className="self-center" />
        {children}
        <Bee.FieldErrors className="hidden data-[errored]:block @2xl:col-start-2" />
        <Bee.FieldHint className="@2xl:col-start-2">{hint}</Bee.FieldHint>
      </div>
    </Bee.FieldProvider>
  )
}

type SettingsToggleFieldProps = {
  label: ReactNode
  name: string | PathBuilder
  errors?: string[]
  children?: ReactNode
  hint?: ReactNode
  className?: string
  hidden?: boolean
}

function SettingsToggleField(props: SettingsToggleFieldProps) {
  const { label: label_, name, errors, children, hint, className, hidden } = props
  const label = label_ ? `${label_}:` : label_

  // avoid rendering the field if it is hidden so that in the form submission it is not included
  if (hidden) return null

  return (
    <Bee.FieldProvider label={label} name={name} errorMessages={errors}>
      <div className={className}>
        <div className="grid grid-cols-[auto,1fr] items-start gap-x-2 @2xl:items-center">
          <div className="shrink-0">{children}</div>

          <div className="leading-tight @2xl:flex @2xl:flex-row @2xl:items-center">
            <Bee.FieldLabel className="whitespace-nowrap font-medium" />
            <Bee.FieldHint className="ml-2 text-base">{hint}</Bee.FieldHint>
          </div>
        </div>

        <div className="self-center">
          <Bee.FieldErrors className="hidden data-[errored]:block" />
        </div>
      </div>
    </Bee.FieldProvider>
  )
}
// #endregion
